import type { PixelClient } from "../client.js";
import { pageContext } from "./page-context.js";
import { scrollMetrics } from "./scroll-metrics.js";

const MILESTONES = [25, 50, 75, 100] as const;

/** Emit scroll_depth when the visitor crosses 25/50/75/100% of the page. */
export function attachScrollDepth(
  client: PixelClient,
  onDepth?: (pct: number) => void,
): () => void {
  let maxSent = 0;
  let raf = 0;

  const check = () => {
    const { scrollable, pct } = scrollMetrics();
    if (scrollable <= 0) return;

    for (const milestone of MILESTONES) {
      if (pct >= milestone && maxSent < milestone) {
        maxSent = milestone;
        onDepth?.(milestone);
        void client.track("scroll_depth", { depth_pct: milestone, max_depth_pct: pct }, pageContext());
      }
    }
  };

  const schedule = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      check();
    });
  };

  // Window and document both — some themes/browsers fire scroll on only one.
  window.addEventListener("scroll", schedule, { passive: true });
  document.addEventListener("scroll", schedule, { passive: true });
  check();

  return () => {
    window.removeEventListener("scroll", schedule);
    document.removeEventListener("scroll", schedule);
    if (raf) cancelAnimationFrame(raf);
  };
}

export function scrollDepthPct(): number {
  return scrollMetrics().pct;
}
